import React, { useEffect } from "react";
import type { FloorplanScene } from "@mvk/shared";
import { FLOORPLAN_KIND_STYLE, normalizeBoothCode, sceneHasContent } from "@mvk/shared";

type Props = {
  scene: FloorplanScene;
  className?: string;
  /** Show booth codes on cells (admin / labeled visitor mode). */
  showBoothCodes?: boolean;
  /**
   * Extra titles by booth code (from exhibitors).
   * Merged with names extracted from the Corel/SVG cell.
   */
  boothTitles?: Record<string, string>;
  /**
   * Overlay on SVG underlay: no page fill/grid, translucent cells so Corel shows through.
   */
  overlay?: boolean;
  /** Hide booth rectangles (only arch markers) — underlay already has booth drawing. */
  boothsOnlyArch?: boolean;
  /** Label size multiplier (0.7–2.2). Default 1. */
  labelScale?: number;
  onReady?: () => void;
};

function wrapLines(text: string, maxChars: number, maxLines: number): string[] {
  const words = text.replace(/\s+/g, " ").trim().split(" ");
  if (!words.length || !words[0]) return [];
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= maxChars) {
      cur = next;
      continue;
    }
    if (cur) lines.push(cur);
    cur = w;
    if (lines.length >= maxLines - 1) break;
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  return lines;
}

/**
 * Our own schematic — not Corel SVG underlay.
 * Labels mimic Corel: code top-left, company name centered in the cell.
 */
export function FloorplanSceneView({
  scene,
  className = "",
  showBoothCodes = true,
  boothTitles,
  overlay = false,
  boothsOnlyArch = false,
  labelScale = 1,
  onReady,
}: Props) {
  useEffect(() => {
    if (sceneHasContent(scene)) onReady?.();
  }, [scene, onReady]);

  if (!sceneHasContent(scene)) return null;

  const vb = `0 0 ${scene.width} ${scene.height}`;
  const gridStep = Math.max(12, Math.min(scene.width, scene.height) / 40);
  const scale = Math.max(0.7, Math.min(2.2, labelScale));
  const objects = boothsOnlyArch
    ? scene.objects.filter((o) => o.kind !== "booth")
    : scene.objects;

  return (
    <svg
      viewBox={vb}
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      className={`absolute inset-0 block ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Схема экспозиции"
    >
      {!overlay ? (
        <>
          <defs>
            <pattern id="fp-grid" width={gridStep} height={gridStep} patternUnits="userSpaceOnUse">
              <path
                d={`M ${gridStep} 0 L 0 0 0 ${gridStep}`}
                fill="none"
                stroke="#c7d7ea"
                strokeWidth={0.6}
              />
            </pattern>
          </defs>
          <rect x={0} y={0} width={scene.width} height={scene.height} fill="#eef4fb" />
          <rect x={0} y={0} width={scene.width} height={scene.height} fill="url(#fp-grid)" />
        </>
      ) : (
        <rect x={0} y={0} width={scene.width} height={scene.height} fill="transparent" />
      )}

      {objects.map((o) => {
        const style = FLOORPLAN_KIND_STYLE[o.kind] ?? FLOORPLAN_KIND_STYLE.other;
        const fill = overlay
          ? o.kind === "booth"
            ? "rgba(99,102,241,0.12)"
            : style.fill
          : style.fill;
        const x = (o.x / 100) * scene.width;
        const y = (o.y / 100) * scene.height;
        const w = (o.w / 100) * scene.width;
        const h = (o.h / 100) * scene.height;
        const code = o.code ? normalizeBoothCode(o.code) : "";
        const fromExhibitor = code && boothTitles ? boothTitles[code] : undefined;
        const fromScene =
          o.label && normalizeBoothCode(o.label) !== code ? o.label.trim() : "";
        const title = (fromExhibitor || fromScene || "").trim();
        const showCode = Boolean(showBoothCodes && code && o.kind === "booth");
        const archLabel = o.kind !== "booth" ? o.label || o.code || "" : "";

        const pad = Math.max(2, Math.min(w, h) * 0.06);
        // Uniform label size across all cells — only global labelScale changes it.
        // Uniform type for booth code, company name and tech labels.
        const codeSize = 12 * scale;
        const titleSize = 12 * scale;
        const archSize = 12 * scale;
        const maxChars = Math.max(4, Math.floor(w / (titleSize * 0.55)));
        const maxLines = h < 28 ? 1 : h < 48 ? 2 : 4;
        const titleLines =
          o.kind === "booth" && title ? wrapLines(title, maxChars, maxLines) : [];
        const archMaxChars = Math.max(4, Math.floor(w / (archSize * 0.55)));
        const archLines = archLabel
          ? wrapLines(archLabel, archMaxChars, h < 28 ? 2 : 4)
          : [];

        return (
          <g key={o.id}>
            <rect
              x={x}
              y={y}
              width={Math.max(2, w)}
              height={Math.max(2, h)}
              fill={fill}
              stroke={style.stroke}
              strokeWidth={Math.max(0.9, scene.width * 0.0015)}
              rx={6}
              ry={6}
            />
            {o.kind === "booth" && showCode && titleLines.length ? (
              <text
                x={x + pad}
                y={y + pad + codeSize * 0.85}
                textAnchor="start"
                fill={style.text}
                fontSize={codeSize}
                fontFamily="system-ui, Segoe UI, sans-serif"
                fontWeight={600}
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {code}
              </text>
            ) : null}
            {o.kind === "booth" && titleLines.length ? (
              <text
                x={x + w / 2}
                y={
                  y +
                  h / 2 -
                  ((titleLines.length - 1) * titleSize * 1.1) / 2 +
                  (showCode ? codeSize * 0.1 : 0)
                }
                textAnchor="middle"
                fill={style.text}
                fontSize={titleSize}
                fontFamily="system-ui, Segoe UI, sans-serif"
                fontWeight={600}
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {titleLines.map((line, li) => (
                  <tspan key={li} x={x + w / 2} dy={li === 0 ? 0 : titleSize * 1.15}>
                    {line}
                  </tspan>
                ))}
              </text>
            ) : null}
            {o.kind === "booth" && showCode && !titleLines.length ? (
              <text
                x={x + w / 2}
                y={y + h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={style.text}
                fontSize={codeSize}
                fontFamily="system-ui, Segoe UI, sans-serif"
                fontWeight={600}
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {code}
              </text>
            ) : null}
            {o.kind !== "booth" && archLines.length ? (
              <text
                x={x + w / 2}
                y={
                  y +
                  h / 2 -
                  ((archLines.length - 1) * archSize * 1.15) / 2
                }
                textAnchor="middle"
                fill={style.text}
                fontSize={archSize}
                fontFamily="system-ui, Segoe UI, sans-serif"
                fontWeight={600}
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {archLines.map((line, li) => (
                  <tspan key={li} x={x + w / 2} dy={li === 0 ? 0 : archSize * 1.15}>
                    {line}
                  </tspan>
                ))}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
